<template>
  <!-- XOL-67 (B4): on an RTA the opener says WHY it is disabled (`button-title`, UX revised section 4), and the drawer stays
       open until the server has answered (`keep-open-until-saved`), so a refusal keeps what was typed. A legacy site
       (no `rta-data`) behaves as it always did. -->
  <popup
    ref="popup"
    buttonStyle="success"
    :button-text="addSuffix (nextNo, false) +' Option'"
    heading="New Option"
    buttonIcon="fa fa-plus"
    :keep-open-until-saved="isRta"
    :button-title="addReason || null"
    @open="onOpen"
    @save="onSave"
    @cancel="onCancel"
    :before-save="checkBeforeSave"
    :disableButton="disableButton" >


    <!-- XOL-67 (V2, U-7): adding in Client Review sends the RTA back to Searching: said first, before the form. -->
    <el-alert
      v-if="sendsBackToSearching"
      class="rta-add-warning"
      type="warning"
      show-icon
      :closable="false"
      title="Adding an option sends this RTA back to Searching"
      description="The options in review go back to Draft, reviews that weren't submitted are discarded, and the options must be proposed again." />

    <!-- The request's point is the crosshairs on the map and the option's pin starts on it; the request's fields are copied in (decision 158 Q2). -->
    <acquire-details :key="formKey" :row-data="rta" :site-data="formSite" :rta-data="rtaData" :adding="true" />


  </popup>
</template>


<script>

import { generateRequestId } from '@/lib/axiosClient'

import PostMixins from '@/mixins/PostMixins'
import NumberMixins from '@/mixins/NumberMixins'
import RtaCommandMixins from '@/mixins/RtaCommandMixins'

import * as rtaStage from '@/lib/rtaStage'
import * as rtaOption from '@/lib/rtaOption'
import { nominalFieldProblem } from '@/lib/rtaNominal'

import AcquireDetails from '@/components/requests/acquire/AcquireDetails'


export default {

  mixins: [PostMixins, NumberMixins, RtaCommandMixins],

  components: {
   AcquireDetails
  },


  props: {

    siteData: {
      type: Object,
      required: true
    },

    totalOptions: {
      type: Number,
      default: 1
    },

    // The RTA (the action) the option belongs to: a full action. With it the option carries `action_id` and is added to the
    // RTA (XOL-67); without it this is the legacy Candidate-site flow.
    rtaData: {
      type: Object,
      default: null
    },

    // The RTA's live options (Withdrawn, Declined and Not Selected do not count): the limit of 5. Falls back to the
    // RTA's own count, then to the rows listed.
    liveCount: {
      type: Number,
      default: null
    }

  },

  methods: {

    /**
     XOL-675 (4): the new option's ids come from the site, the action and the viewer, never from the
     site's existing options. `site.candidate` is the first existing option's client, so it is null once
     every option has been deleted. The ids used to be set only when it was present, and the POST was
     then refused with "client id / site id required".

     XOL-67 (B4): on an RTA the option has no site (`site_id` stays null and is never sent: the server takes the client
     and the site from the RTA). XOL-67 (V2, v2.1): a new option is PRE-FILLED from the RTA's request (decision 158 Q2,
     P-S33: the SPA's, the server copies nothing): its pin starts on the requested point and its locality, Station Type,
     Structure Type, heights, width and depth are the request's, all editable before Save.
     */
    init () {

      this.rta.options = []
      this.rta.markers = []

      var site = this.isRta ? this.formSite : this.site

      this.rta.site = site

      this.rta.site_id = site.id

      // An RTA carries its own client; otherwise the site's candidate client; a Telco is its own client.
      // A Tower Co with none of these picks the client in the form (AcquireDetails select-sharer).
      var clientId = this.rtaData && this.rtaData.organisation_id
        ? this.rtaData.organisation_id
        : (this.rtaData && this.rtaData.client_id
          ? this.rtaData.client_id
          : (this.site.candidate && this.site.candidate.id
            ? this.site.candidate.id
            : (this.isTelco() ? this.org().id : null)))

      if (clientId) {
        this.rta.client_id = clientId
      }

      if (this.rtaData && this.rtaData.id) {
        this.rta.action_id = this.rtaData.id
      }

      this.rta.optionNo = this.nextNo

      if (this.isRta) {
        this.prefill()
        return
      }

      // The legacy Candidate site: the site's own point and structure.
      this.rta.actualLat = Number(this.site.latitude)
      this.rta.actualLng = Number(this.site.longitude)

      if (this.site.structure) {
        this.rta.structure_id = this.site.structure.structure_id
        this.rta.structureHeight = this.site.structure.height
      }

    },


    /**
     An RTA's new option, from the request: the keys rtaOption.optionDefaultsFromRequest gives (the point as `actualLat` / `actualLng`, the locality,
     the requirements; only what the request holds). The option's pin IS that point (decisions 144, 155: there is no option-type marker any more), drawn
     by the form's map in the colour of the option's number; a request with no point leaves the option without one: the user places it. `markers` starts
     empty; the form's on-map panel adds extra markers (decision 187), which go with the create.
     */
    prefill () {
      var self = this
      var defaults = rtaOption.optionDefaultsFromRequest(rtaStage.rtaOf(this.rtaData))

      Object.keys(defaults).forEach(function (k) {
        self.$set(self.rta, k, defaults[k])
      })

      // A request with no point: the option has none either. The table's scaffold holds 0 / 0 (the columns' default), which the inputs would show and
      // the add would store as a point in the Gulf of Guinea; null until the user places the pin.
      if (!Object.prototype.hasOwnProperty.call(defaults, 'actualLat')) {
        this.$set(this.rta, 'actualLat', null)
        this.$set(this.rta, 'actualLng', null)
      }

      this.rta.markers = []
    },


    // What the option carries when it is sent. An RTA's option (v2.1): the point and the locality are whatever the form set, and the extra markers added
    // in the form (decision 187). Its distance from the request is the SERVER's (P-S6): not computed, not sent. The legacy Candidate site keeps what it always did: the
    // locality, the option marker and its distance from the site's point.
    prepare () {

      if (this.isRta) {
        this.prepareRta()
        return
      }

      this.rta.region = this.setProp(this.rta.region, this.loc[0])
      this.rta.state = this.setProp(this.rta.state, this.loc[1])
      this.rta.district = this.setProp(this.rta.district, this.loc[2])
      this.rta.subdistrict = this.setProp(this.rta.subdistrict, this.loc[3])
      this.rta.zone = this.setProp(this.rta.zone, this.loc[4])


      if (this.site.latitude && this.site.longitude) {
        this.rta.markers = [
          {infoText: 'Option ' + this.rta.optionNo,
            icon: rtaOption.optionMarkerLook(this.rta.optionNo).icon,
            type: 'option',
            distance: this.roundTo(this.distanceInKmBetween(Number(this.site.latitude), Number(this.site.longitude), this.rta.actualLat, this.rta.actualLng) * 1000, 2),
            position: {lat: this.rta.actualLat, lng: this.rta.actualLng}}
          ]
      } else if (this.rta.markers[0] && this.rta.markers[0].position) {
        // XOL-67 (B11, decision 124): an RTA with no nominal point. The option's own point is the marker the user placed; it has no
        // distance (there is nothing to measure from).
        this.rta.actualLat = Number(this.rta.markers[0].position.lat)
        this.rta.actualLng = Number(this.rta.markers[0].position.lng)
      }

      if (this.rta.markers[0] && this.rta.markers[0].distance != null) {
        this.rta.distanceFromNominal = this.rta.markers[0].distance
      }

    },


    // Decision 187: the extra markers added in the form go with the option (an `option`-type marker is the legacy pin and never does).
    prepareRta () {
      var self = this

      this.rta.markers = (Array.isArray(this.rta.markers) ? this.rta.markers : []).filter(function (m) { return !!m && m.type !== 'option' })

      // No pin is null, never 0 (the float input answers a cleared field with Number('') = 0): the option drawer's own snapshot rule.
      ;['actualLat', 'actualLng'].forEach(function (k) {
        var v = self.rta[k]
        var n = Number(v)

        self.rta[k] = v === null || v === undefined || v === '' || isNaN(n) || n === 0 ? null : n
      })
    },


    // The Popup's check before Save closes anything (decisions 177, 185): the antenna height is empty or a whole number from 0 to 121. A 0 is a value and is
    // sent as 0; a cleared field arrives as null (the input's `empty-is-null`). Anything else is refused, and the drawer stays open with what was typed.
    checkBeforeSave () {
      var problem = nominalFieldProblem('antennaHeight', this.rta.antennaHeight)

      if (problem) {
        this.$message({ message: problem, type: 'error' })
      }

      return !problem
    },


    onSave () {

      this.prepare()

      if (!this.isRta) {
        this.saveForm(this, '/candidate', null, this.rta, (this.rta.title ? this.rta.title : 'Option') + ' #' + this.rta.optionNo, null, null, true, this.tableRef)
        return Promise.resolve(null)
      }

      return this.addToRta()
    },


    /**
     POST /candidate (API E4) with the caller's allow-list (API DEV-10), the RTA's id and ONE idempotency key per click (DEV-26:
     a retry of the same click after a 5xx returns the option it made). A Telco's add sends no e-mail (decision 63).
     */
    addToRta () {
      var self = this
      var party = rtaStage.isTowerCoUser(this.viewerProfile) ? 'tc' : 'client'
      var body = rtaOption.contentBody(this.rta, party, { shared: true, clientRef: true })
      // Taken now: the answer moves the RTA to Searching, and the toast says that this add is what did it (UX spec 4.6).
      var back = this.sendsBackToSearching

      // No point placed: no coordinates in the body (the server's null), as before the scaffold's 0 / 0 was cleared.
      ;['actualLat', 'actualLng'].forEach(function (k) {
        if (body[k] === null) {
          delete body[k]
        }
      })

      body.action_id = this.rtaData.id
      body.idempotencyKey = generateRequestId()

      return this.rtaCommand('post', '/candidate', body, 'Option').then(function (result) {
        var popup = self.$refs.popup

        if (!result.ok) {
          if (popup && typeof popup.saveFailed === 'function') {
            popup.saveFailed()
          }

          var notice = self.rtaRefuse(result, {
            telco: self.rtaData.client ? (self.rtaData.client.name || self.rtaData.client.shortName) : null,
            towerCo: self.towerCoName(),
            role: party === 'tc' ? 'tcc' : 'client',
            command: 'add'
          })

          if (notice && notice.reread) {
            self.$events.emit(self.tableRef + '-refresh-table')
            self.rtaAnnounce(null, self.rtaData.id)
          }

          return result
        }

        var option = result.data.option || {}

        self.$message({ message: rtaOption.sentence('add', {}, { n: option.optionNo || self.rta.optionNo, backToDraft: back }), type: 'success' })

        if (popup && typeof popup.saveSucceeded === 'function') {
          popup.saveSucceeded()
        }

        self.$events.emit(self.tableRef + '-refresh-table')
        self.rtaAnnounce(result.data, self.rtaData.id)
        self.reset()

        return result
      })
    },


    // A fresh form: the next option, with the ids of the RTA.
    reset () {
      this.rta = this.getTableStruct('site_candidates')
      this.formKey++
      this.init()
    },


    // Every open of an RTA's New Option starts from the request AS IT IS NOW (decision 158 Q2): the form was built when this component was created, and
    // the request may have changed since (a Step 1 edit, a manager moving the point). A fresh form, pre-filled again. (The legacy Candidate site keeps
    // what was typed.)
    onOpen () {
      if (this.isRta) {
        this.reset()
      }
    },


    onCancel () {
      this.reset()
    }


  },


  watch: {
    totalOptions: function (total) {
      this.totals = total
      this.rta.optionNo = this.nextNo
    }
  },


  computed: {

    // The store, as computed (not data()): it hydrates after the first paint.
    viewerOrg () {
      return this.org()
    },

    viewerProfile () {
      return this.profile()
    },

    isRta () {
      return !!(this.rtaData && this.rtaData.id)
    },

    // The site the form is for. An RTA has none yet (decision 5): its REQUEST stands in, in the shape of a site, so the option's map has the requested point
    // (the read-only crosshairs) to start from. An RTA whose request is not known here, and the legacy Candidate site, keep the site the caller gave.
    formSite () {
      return rtaOption.requestSite(rtaStage.rtaOf(this.rtaData)) || this.siteData
    },

    // Adding in Client Review returns the RTA to Searching (UX spec 2.3, U-7).
    sendsBackToSearching () {
      return rtaStage.addSendsBackToDraft(rtaStage.rtaOf(this.rtaData))
    },

    // The number of the option the next add makes, and the opener's label ("+ 1st Option", "+ 2nd Option" ...): the RTA's CURRENT count of
    // options + 1 (B12, decision 133, Carlos). The table's total is read again after every add and delete, so it is never stale; under the
    // D133 contract the server numbers its options 1..N (renumbered on a delete), so a count of 3 means the next one is the 4th.
    // PENDING the API: until it ships D133, the server still numbers from its counter, so the label and the created number can differ. It used to read the
    // server's counter (`state.rta.nextOptionNo`, DEV-30), which only went up, and showed "+ 4th Option" over an empty table.
    // The legacy Candidate site keeps its own rule (the total, else the site's count).
    nextNo () {
      if (this.isRta) {
        return (Number(this.totals) || 0) + 1
      }

      return (Number(this.totals ? this.totals : this.site.count) || 0) + 1
    },

    live () {
      if (this.liveCount !== null && this.liveCount !== undefined) {
        return this.liveCount
      }

      var fromRta = this.rtaData ? Number(this.rtaData.liveOptionCount) : NaN

      return isNaN(fromRta) ? (Number(this.totals) || 0) : fromRta
    },

    // Why the opener is disabled, in the words of the UX spec, or '' when it is not (RTA only).
    addReason () {
      if (!this.isRta) {
        return ''
      }

      var c = this.rtaData.client

      return rtaStage.addOptionReason(rtaStage.rtaOf(this.rtaData), this.viewerOrg, this.viewerProfile, this.live, {
        telco: c ? (c.name || c.shortName) : null,
        towerCo: this.towerCoName()
      })
    },

    disableButton: function () {

      if (this.isRta) {
        return this.addReason !== ''
      }

      if (!this.isViewOnly()) {

        if (this.isTelco()) {
          return this.totals >= 5
        } else {
          if (this.isTowerCo()) {
            return !this.isManager('project|property|finance|management|admin') && this.totals >= 5
          }
        }

      }

    return false
    }

  },


  data () {
    return {
      guts: 12,
      loc: [],
      totals: this.totalOptions,

      rta: this.getTableStruct('site_candidates'),
      site: this.siteData,

      // A new AcquireDetails (a new form) after a save or a cancel: it holds the option object it was given.
      formKey: 0,

      tableRef: 'siteCandidatesTable'

    }
  },


  created () {

    this.init()

  }

}

</script>

<style scoped>
.rta-add-warning {
  margin-bottom: 12px;
}
</style>
